import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { AppGlobalsModule } from '../app-globals.module';
import { YeshivaEnums } from 'src/serverTypes/yeshivaTypesEnums';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { LessonsFilterService } from '../content/lessons-filter-service';
import { Content } from '../content/content';

@Injectable({
    providedIn: 'root',
})
export class ContentAreaService implements OnDestroy {
    private lessonsFilterService = inject(LessonsFilterService);

    public area!: YeshivaEnums.AngularApp.API.enums.Area;
    public areaTexts!: AreaTexts;
    /** True for the /nihul (CMS) variants of the areas - the editors open directly in edit mode there */
    public isNihul = false;
    private _isAskAreSubscription: Subscription;
    private filterArrayOfContents: FilteredContents | null = null;

    constructor() {
        this._isAskArea$ = new BehaviorSubject<boolean>(false);
        this._isAskAreSubscription = this.getIsAskArea().subscribe((isAskArea: boolean) => {
            this.isAskArea = isAskArea;
        });
    }

    //init fires from the basic component of the area service (PageWrapperComponent)
    public init() {
        //init the catName title
        this.setIconsPath();
        this.areaBase = this.getAreaBase();
        this.setIsAskArea(this.areaBase === YeshivaEnums.AngularApp.API.enums.Area[YeshivaEnums.AngularApp.API.enums.Area.ask]);

        //init the filter
        this.lessonsFilterService.resetFilter(false);
    }

    // Signal-backed so OnPush templates reading it directly are tracked; the property syntax stays unchanged.
    private readonly _isAskArea = signal(false);
    public get isAskArea(): boolean {
        return this._isAskArea();
    }
    public set isAskArea(isAskArea: boolean) {
        this._isAskArea.set(isAskArea);
    }
    public areaBase: string = '';
    private _isAskArea$: BehaviorSubject<boolean>;
    public getIsAskArea(): Observable<boolean> {
        return this._isAskArea$.asObservable();
    }
    public setIsAskArea(isAskArea: boolean): void {
        this._isAskArea$.next(isAskArea);
    }

    public apiBase(): string {
        return YeshivaEnums.AngularApp.API.enums.Area[this.area];
    }

    public getAreaBase(): string {
        return YeshivaEnums.AngularApp.API.enums.Area[this.area];
    }

    /**
     * set FilterArrayOfContents
     *
     * @param contents Content that comes from a filter page
     * @param moreIn // The title that will appear above the contents that will be displayed as 'More In'
     * @param link //The title link
     */
    public setFilterArrayOfContents(contents: Content[], moreIn: string, link: string) {
        this.filterArrayOfContents = {
            contents: contents,
            title: moreIn,
            link: link,
        };
    }

    public setFilterArrayOfContentsNull() {
        //set null.
        this.filterArrayOfContents = null;
    }

    public getFilterArrayOfContents(): FilteredContents | null {
        return this.filterArrayOfContents;
    }

    public anotherAreaBase(): string {
        return YeshivaEnums.AngularApp.API.enums.Area[this.area] === YeshivaEnums.AngularApp.API.enums.Area[YeshivaEnums.AngularApp.API.enums.Area.midrash]
            ? YeshivaEnums.AngularApp.API.enums.Area[YeshivaEnums.AngularApp.API.enums.Area.ask]
            : YeshivaEnums.AngularApp.API.enums.Area[YeshivaEnums.AngularApp.API.enums.Area.midrash];
    }

    //api/{apiBase}/(request)
    public getContentApiPath(request: string, urlBase: string = ''): string {
        return AppGlobalsModule.apiPath(request, urlBase ? urlBase : this.apiBase());
    }

    //factory method to change content-area router components to area-specific implementations
    //DONT! use it to change deeper routings components (like in home)
    public areaRouterComponentFactory(component: any): any {
        //to implement in extenders
    }

    public getAreaTitle(): any {
        return this.areaTexts.title;
    }

    public getContentType(): YeshivaEnums.YeshivaModels.BE.EntityType {
        return YeshivaEnums.YeshivaModels.BE.EntityType.NONE;
    }

    public getReplyType(): YeshivaEnums.AngularApp.API.enums.ReplyType {
        return YeshivaEnums.AngularApp.API.enums.ReplyType.NONE;
    }

    public displayShabbat(): boolean {
        return false;
    }

    public getSideLinks(): any {
        return null;
    }

    public setIconsPath(): void {}

    ngOnDestroy(): void {
        if (this._isAskArea$) this._isAskArea$.unsubscribe();
        if (this._isAskAreSubscription) this._isAskAreSubscription.unsubscribe();
    }
}

export class AreaTexts {
    public rabbisArea!: string; // רבנים ב..
    public title!: string;
    public pageTitlePostfix!: string;
    public contentName!: string; //שיעור
    public anotherContentName!: string; //שיעור
    public contentContent!: string; //תוכן השיעור/שאלה
    public contentsName!: string; //שיעורים
    public anotherContentsName!: string; //שיעורים
    public contentsOf!: string; //שיעורי
    public rabbisContents!: string;
    public morePopularContnetsOnTopic!: string;
    public halachaYomit!: string;
    popularContents!: string;
    recentContents!: string;
    moreContents!: string;
    contentsNotFoundForThatQuery!: string;
    rabbisListTitle!: string;
    moreContentsOn!: string;
    defaultPageDescription!: string;
    defaultPageKeywords!: string;
    rootUrl!: string;
}

export class FilteredContents {
    //For content that comes from a filter page
    public contents!: Content[];
    public title!: string;
    public link!: string;
}
